/* ───────────────────────────────────────────────────────────────────────────
   assets/puente-landing-co.js — EL PUENTE ENTRE LA LANDING Y EL FORMULARIO COD-CO.

   🔴 ARCHIVO NUEVO DEL REPO (02-sep). Hasta hoy este puente vivía dentro de un
   bloque `custom_liquid` de la plantilla del tema: código que gobierna por dónde
   entra CADA pedido colombiano y que no estaba en git — sin historia, sin revisión
   y sin forma de saber qué versión corre. Se muda aquí tal cual (IIFE, sin imports,
   se sirve como asset del tema) y se le añade lo que faltaba: LA HERENCIA.

   ═══════════════════════════════════════════════════════════════════════════
   QUÉ RESUELVE
   ═══════════════════════════════════════════════════════════════════════════
   En la página conviven DOS piezas que no se conocen:
     · el BLOQUE DE OFERTAS MEXICANO (snippets/landing-buda-offers-inline.liquid +
       assets/landing-buda.js), que pinta la escalera de packs y las tarjetas de
       adicionales ARRIBA, en la propia landing;
     · el FORMULARIO COLOMBIANO (cod-co.liquid + cod-co.js), que es quien de verdad
       cotiza y crea el pedido.
   El cliente elegía "HOY 2X1" fuera, pulsaba la píldora… y el formulario abría en
   "1 Pulsera". Dos selecciones independientes sobre la misma compra: el cliente
   creía llevar dos y pedía una. Lo mismo con el Empaque Premium marcado fuera.

   Este puente hace DOS cosas, en este orden:
     (1) INTERCEPTA la píldora de la landing para que abra el formulario COLOMBIANO
         en vez del overlay mexicano (que es lo que ya hacía el custom_liquid);
     (2) HEREDA la selección: el tier (unidades) y los adicionales marcados.

   ═══════════════════════════════════════════════════════════════════════════
   🔒 LA REGLA DURA: SE HEREDA POR CLICS, NUNCA ESCRIBIENDO ESTADO
   ═══════════════════════════════════════════════════════════════════════════
   Todo lo que este archivo hace sobre el formulario son las MISMAS acciones que
   haría el cliente con el dedo: `.click()` sobre la tarjeta del tier, sobre el `±`
   del stepper y sobre la casilla del adicional. NUNCA se tocan las variables
   internas de cod-co.js (`tierIdx`, `unidadesElegidas`, `ticksState`) ni se marca
   un checkbox por atributo.

   No es purismo: los manejadores de cod-co.js son los que repintan la pantalla Y
   los que llaman a `programarCotizacion()`. Saltárselos dejaría el pedido con un
   tier y la pantalla —y el total del botón— mostrando otro, que es exactamente la
   clase de divergencia que este carril tiene prohibida. Como cada clic recotiza y
   `programarCotizacion` está deducido a 250 ms, toda la herencia colapsa en UNA
   sola llamada a /cotizar-co.

   🔒 Y NO SE INVENTA UN PESO. Este archivo no lee ni un precio, no suma, no
   compara importes: sólo mueve UNIDADES y VOTOS de adicional. El dinero lo sigue
   diciendo el servidor por /cotizar-co, y el submit sigue bloqueado hasta que
   conteste.

   ═══════════════════════════════════════════════════════════════════════════
   ⚠️ EL CLIENTE QUE PULSA ANTES DE QUE TODO ESTÉ MONTADO
   ═══════════════════════════════════════════════════════════════════════════
   Es el caso normal en móvil con red lenta, no una rareza. Lo que hace este código,
   caso por caso:

   · El listener es DELEGADO en `document` y en fase de CAPTURA, y se registra en
     cuanto el script se parsea. No busca la píldora al arrancar, así que da igual
     que el bloque de ofertas se monte después: el clic se atiende igual.

   · SI cod-co.js TODAVÍA NO ARRANCÓ (`[data-cco-root]` ausente, o sin la marca
     `ccoListo`), el puente NO intercepta: deja pasar el clic y la página se comporta
     EXACTAMENTE como hoy. Interceptar ahí sería tragarse el clic y dejar al cliente
     con una píldora muerta — peor que no tener puente.

   · SI LA LANDING NO HA PINTADO SU SELECCIÓN, no se hereda nada y el formulario
     abre como hoy (fail-safe). En la práctica el tier casi nunca falta: el `is-active`
     del tier "popular" lo escribe LIQUID en el servidor, así que existe desde el
     primer pintado, antes de que corra un solo JS. Las tarjetas de adicionales, en
     cambio, las pinta landing-buda.js: si el cliente pulsa antes, no hay ninguna
     casilla que leer y los adicionales se quedan en su valor por defecto — que es la
     dirección segura (nunca se añade un extra que el cliente no vio marcado).

   · SI EL FORMULARIO NO OFRECE ESAS UNIDADES, no se fuerza nada: se abre como hoy.

   Ningún camino de este archivo puede impedir que el modal se abra: la herencia va
   DESPUÉS del clic de apertura y entera dentro de un `try`.
   ─────────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  // Guard de doble inclusión (el tema podría dejar el custom_liquid viejo y el asset
  // nuevo a la vez durante un despliegue). Sin esto habría dos interceptores: el
  // primero para el clic y el segundo no vería nada, pero el modal se abriría dos
  // veces y `abrir()` recotiza en cada apertura.
  var raiz = document.documentElement;
  if (!raiz || raiz.getAttribute('data-cco-puente') === '1') return;
  raiz.setAttribute('data-cco-puente', '1');

  // Las dos píldoras de la landing que abren el pedido: el CTA principal del bloque
  // de ofertas y el botón de la barra adhesiva. Son los mismos anclajes que
  // intercepta initCod/initCodV2 en México (landing-buda.js), y por eso hay que
  // ganarles: ellos abren el overlay mexicano.
  var SEL_PILDORA = '[data-lb-cod-cta], [data-lb-sticky-btn]';

  // Tope de seguridad del stepper. NO es una copia del cap del negocio (ése vive en
  // CAP_UNIDADES de cod-co.js y en OPEN_TIER_CAP_CO del servidor): es sólo el freno
  // del bucle. La condición REAL de parada es que el número del stepper deje de
  // MOVERSE, así que si mañana el cap (o el mínimo del pack) cambia, este archivo no
  // hay que tocarlo.
  var MAX_PASOS = 40;

  function cerrado(el, sel) {
    return (el && el.closest) ? el.closest(sel) : null;
  }

  function texto(el) {
    return el ? String(el.textContent || '').replace(/\s+/g, ' ').trim() : '';
  }

  // Comparación de títulos de adicional entre las dos pantallas. Se normaliza lo
  // mínimo (espacios y mayúsculas); los acentos NO se quitan a propósito: los dos
  // lados leen el MISMO campo del MISMO metafield, así que una diferencia de acento
  // significaría que no son el mismo adicional.
  function claveTitulo(s) {
    return String(s == null ? '' : s).replace(/\s+/g, ' ').trim().toLowerCase();
  }

  // ── LO QUE SE LEE DE LA LANDING ──────────────────────────────────────────
  // Un SNAPSHOT, tomado en el instante del clic. No se observa la landing ni se
  // sincroniza después: a partir de aquí manda el formulario, que es el único sitio
  // donde el cliente ve el total que va a pagar.
  function leerLanding() {
    // TIER ACTIVO. `.lb-tier.is-active` es la tarjeta seleccionada (landing-buda.js
    // `selectTier` mueve la clase; Liquid ya la nace en el tier `popular`). Los dos
    // respaldos replican lo que haría `selectTier` en su primera pasada, para el caso
    // del clic tempranísimo.
    var tier = document.querySelector('.lb-tier.is-active') ||
               document.querySelector('.lb-tier[data-tier-popular="true"]') ||
               document.querySelector('.lb-tier');

    var unidades = null;
    if (tier) {
      // 🔑 EN EL TIER ABIERTO LAS UNIDADES SON LAS DEL STEPPER, NO LAS DEL TIER.
      // `data-tier-unidades` es lo que landing-buda.js mantiene al mover el ± del
      // pack abierto ("3 o más"); `data-tier-n` es el mínimo del pack. Leer sólo `n`
      // heredaría 3 cuando el cliente dejó 5 fuera.
      var abiertas = parseInt(tier.getAttribute('data-tier-unidades'), 10);
      var n = parseInt(tier.getAttribute('data-tier-n'), 10);
      if (isFinite(abiertas) && abiertas > 0) unidades = abiertas;
      else if (isFinite(n) && n > 0) unidades = n;
    }

    // ADICIONALES. La fuente de verdad es el `<input type=checkbox>` de cada tarjeta
    // (`[data-lb-tick-idx]`), no la clase `is-on` de la tarjeta: la clase es piel.
    var ticks = [];
    var cbs = document.querySelectorAll('[data-lb-tick-idx]');
    for (var i = 0; i < cbs.length; i++) {
      var cb = cbs[i];
      var idx = parseInt(cb.getAttribute('data-lb-tick-idx'), 10);
      if (!(idx >= 0)) continue;
      // El título visible de la tarjeta, para poder cruzarlo con el del formulario
      // antes de votar. Sin tarjeta (o sin título) se guarda '' y ese índice no se
      // hereda: no se vota a ciegas sobre algo que cuesta dinero.
      var tarjeta = cerrado(cb, '[data-lb-tickcard]');
      var tit = tarjeta ? tarjeta.querySelector('.lb2-tickcard__title') : null;
      ticks.push({ idx: idx, on: cb.checked === true, titulo: texto(tit) });
    }
    return { unidades: unidades, ticks: ticks };
  }

  // ── LLEVAR EL STEPPER DEL TIER ABIERTO A `unidades` ──────────────────────
  // En LOS DOS SENTIDOS, y eso importa: el estado de cod-co.js sobrevive a cerrar y
  // reabrir el modal, así que un cliente que dejó 5 dentro y luego eligió 3 fuera
  // volvería a entrar en 5. Heredar es COPIAR la selección, no sólo empujarla hacia
  // arriba.
  //
  // 🔒 LA PARADA LA DICE EL PROPIO STEPPER. Se compara el número pintado antes y
  // después de cada clic: si deja de moverse es que se tocó el techo (el cap que pone
  // cod-co.js y vuelve a poner el servidor) o el piso (el mínimo del pack), y se para.
  // Así este archivo no guarda ninguna copia de esos límites, y un clic en `+`/`−`
  // nunca puede producir una cantidad inválida: el control ya los conoce.
  function moverStepperA(cco, unidades) {
    var toco = false;
    var pasos = 0;
    while (pasos++ < MAX_PASOS) {
      var cant = cco.querySelector('[data-cco-cant]');
      var actual = cant ? parseInt(texto(cant), 10) : NaN;
      if (!isFinite(actual) || actual === unidades) break;
      var sube = actual < unidades;
      var boton = cco.querySelector(sube ? '[data-cco-mas]' : '[data-cco-menos]');
      if (!boton || boton.disabled) break;
      boton.click();
      // El stepper se repinta entero en cada clic: hay que volver a leer el nodo.
      var cant2 = cco.querySelector('[data-cco-cant]');
      var nuevo = cant2 ? parseInt(texto(cant2), 10) : NaN;
      // Si no se movió HACIA el objetivo, se llegó al límite: se para donde el propio
      // control manda, sin insistir.
      if (!isFinite(nuevo) || (sube ? nuevo <= actual : nuevo >= actual)) break;
      toco = true;
    }
    return toco;
  }

  // ── APLICAR EL TIER ──────────────────────────────────────────────────────
  // Devuelve true si tocó algo. `cco` aquí es el [data-cco-root] del formulario.
  function aplicarTier(cco, unidades) {
    if (!(unidades > 0)) return false;   // la landing no dijo nada: no se fuerza nada

    var botones = cco.querySelectorAll('[data-cco-tier][data-cco-tier-n]');

    // CASO SIN ESCALERA: un producto con UN SOLO tier y ABIERTO no pinta tarjetas de
    // tier —cod-co.js dibuja sólo el stepper (`cco-tier-card--solo`)—, así que no hay
    // ningún botón que elegir… pero las unidades sí se pueden heredar, que es de lo
    // que va todo esto. Si además no hubiera stepper (tier único y CERRADO), `moverStepperA`
    // no encuentra nada y no hace nada: sigue sin forzarse nada.
    if (!botones.length) return moverStepperA(cco, unidades);
    var exacto = null;
    var abierto = null;   // el tier ABIERTO más alto que sigue estando por debajo

    for (var i = 0; i < botones.length; i++) {
      var b = botones[i];
      var n = parseInt(b.getAttribute('data-cco-tier-n'), 10);
      if (!(n > 0)) continue;
      if (n === unidades) { exacto = b; break; }
      // Un tier ABIERTO ("3 o más") SÍ ofrece esas unidades: se llega con su propio
      // stepper. No es forzar nada — es el mismo camino que tiene el dedo del cliente.
      // Si hubiera varios abiertos por debajo se elige el más alto, que es el que el
      // cliente vería como su pack (y el que da el mejor precio por pieza).
      if (b.getAttribute('data-cco-tier-abierto') === '1' && n < unidades) {
        var nAb = abierto ? parseInt(abierto.getAttribute('data-cco-tier-n'), 10) : -1;
        if (n > nAb) abierto = b;
      }
    }

    var elegido = exacto || abierto;
    // 🔒 SIN COINCIDENCIA NO SE FUERZA NADA. Unidades que el formulario no ofrece
    // (o un producto sin escalera) → se abre como hoy, con su tier por defecto.
    if (!elegido) return false;

    var toco = false;
    // Sólo se pulsa si NO está ya seleccionado: un clic redundante reinicia
    // `unidadesElegidas` al mínimo del pack y gastaría una cotización de más.
    if (elegido.getAttribute('aria-pressed') !== 'true') { elegido.click(); toco = true; }
    // 🔴 HEREDAR ES COPIAR, TAMBIÉN HACIA ABAJO. Salir aquí sin más dejaba vivo el
    // caso: el cliente entra, sube a 5 con el `+`, cierra; la landing sigue diciendo
    // 3 (el tier abierto "3 o más"); vuelve a pulsar → coincidencia EXACTA con ese
    // mismo tier, ya seleccionado → se salía → el pedido se cotizaba y se enviaba por
    // 5. `abrir()` no reinicia `unidadesElegidas` (cod-co.js), así que el estado
    // sobrevive a cerrar el modal. No es una divergencia oculta —lo pintado y el
    // payload coinciden, el 5 se ve— pero es dinero de más respecto de lo que el
    // cliente eligió FUERA, que es lo que la píldora le prometió.
    // Solo el tier ABIERTO necesita el paso: en uno cerrado el .click() ya dejó
    // `unidadesElegidas = n`.
    if (exacto) {
      return (elegido.getAttribute('data-cco-tier-abierto') === '1')
        ? (moverStepperA(cco, unidades) || toco)
        : toco;
    }

    // Tier abierto elegido: subir con el `+` real hasta las unidades de la landing.
    return moverStepperA(cco, unidades) || toco;
  }

  // ── APLICAR LOS ADICIONALES ──────────────────────────────────────────────
  // 🔒 SE HEREDAN LAS DOS DIRECCIONES, Y ESO NO ES UN CAPRICHO. Heredar sólo lo
  // marcado dejaría un agujero de dinero: un adicional con `marcadoDefault` que el
  // cliente DESMARCÓ fuera volvería a nacer marcado dentro y se le cobraría un extra
  // que él ya había quitado. Se copia la casilla tal cual está en la landing.
  //
  // 🔒 EL ÍNDICE ES EL CONTRATO… Y SE VERIFICA CON EL TÍTULO. Las dos pantallas
  // normalizan `landing.upsellTick` igual (objeto→[objeto], `slice(0,4)`, sin
  // compactar huecos), así que el índice ya debería bastar. Pero votar por el índice
  // equivocado significa cobrarle al cliente OTRO adicional, así que se cruza con el
  // título: si no coinciden —o si alguno de los dos lados no lo publica— ese índice
  // NO se toca. Fail-safe hacia no votar.
  function aplicarTicks(cco, ticks) {
    var toco = false;
    for (var i = 0; i < ticks.length; i++) {
      var t = ticks[i];
      var cb = cco.querySelector('[data-cco-tick="' + t.idx + '"]');
      if (!cb || cb.disabled) continue;

      var tarjeta = cerrado(cb, '[data-cco-tickcard]');
      var tituloCo = tarjeta ? tarjeta.getAttribute('data-cco-tick-titulo') : null;
      if (!tituloCo || !t.titulo) continue;                       // no verificable
      if (claveTitulo(tituloCo) !== claveTitulo(t.titulo)) continue;  // no es el mismo

      if ((cb.checked === true) === (t.on === true)) continue;     // ya coincide
      cb.click();   // dispara el `change` de cod-co.js → repinta, vota y recotiza
      toco = true;
    }
    return toco;
  }

  // ── EL INTERCEPTOR ───────────────────────────────────────────────────────
  // Fase de CAPTURA sobre `document`: landing-buda.js engancha sus manejadores en el
  // propio botón (fase de burbuja), y la captura en `document` corre SIEMPRE antes.
  // Por eso `stopPropagation()` basta para que el overlay mexicano no llegue a
  // abrirse; `stopImmediatePropagation()` cubre además a cualquier otro oyente que
  // se hubiera registrado en `document` después de éste.
  document.addEventListener('click', function (ev) {
    var pildora = cerrado(ev.target, SEL_PILDORA);
    if (!pildora) return;

    var cco = document.querySelector('[data-cco-root]');
    var abrir = cco ? cco.querySelector('[data-cco-abrir]') : null;
    // 🔒 SI EL FORMULARIO NO ESTÁ VIVO, NO SE INTERCEPTA. `ccoListo` lo estampa
    // cod-co.js al arrancar: sin él, el botón de abrir es un botón muerto y tragarse
    // el clic dejaría al cliente sin ninguna forma de comprar. Se deja pasar y la
    // página hace lo de hoy.
    if (!cco || !abrir || cco.dataset.ccoListo !== '1') return;

    ev.preventDefault();
    ev.stopPropagation();
    if (ev.stopImmediatePropagation) ev.stopImmediatePropagation();

    // El snapshot se toma ANTES de abrir. Abrir no mueve la landing, pero leer
    // primero deja la intención del cliente congelada aunque algo repinte por debajo.
    var sel = null;
    try { sel = leerLanding(); } catch (e) { sel = null; }

    // Abrir SIEMPRE, y por el mismo botón que pulsaría el cliente: `abrir()` pinta la
    // escalera, los adicionales y arranca la cotización.
    abrir.click();

    // La herencia va entera dentro de un `try`: pase lo que pase aquí, el modal ya
    // está abierto y el formulario funciona como hoy. Nunca al revés.
    try {
      if (!sel) return;
      // `abrir()` pinta de forma SÍNCRONA (pintarTiers / pintarUnidades / pintarTicks),
      // así que a esta altura los nodos ya existen. Si algún día dejara de serlo, los
      // `querySelector` de abajo devuelven null y no se hereda nada: fail-safe, nunca
      // una selección a medias.
      aplicarTier(cco, sel.unidades);
      // 🔑 DESPUÉS del tier, y no antes: `aplicarTier` cambia las unidades del pedido y
      // eso REPINTA los chips del formulario (pintarUnidades). Aplicar las variantes
      // primero escribiría sobre botones que dejan de existir un instante después.
      aplicarVariantes(cco);
      aplicarTicks(cco, sel.ticks);
      // No hace falta recotizar a mano: cada clic aplicado ya llamó a
      // `programarCotizacion()` (deducido 250 ms → una sola llamada), y si no se
      // aplicó ninguno, la cotización que arrancó `abrir()` sigue siendo la buena.
    } catch (e) { /* la herencia es un extra: jamás puede costar la venta */ }
  }, true);

  /* ═══════════════════════════════════════════════════════════════════════════
     (2) LA CAPA DE PRESENTACIÓN SOBRE EL BLOQUE MEXICANO
     ═══════════════════════════════════════════════════════════════════════════
     🚨 ESTO ES UNA RESTAURACIÓN, Y LA REGRESIÓN LA CAUSÓ ESTE MISMO ARCHIVO.
     Hasta el 02-sep esta capa vivía dentro del `custom_liquid` de la plantilla, junto
     al interceptor. Al mudar el puente al repo se trajo el interceptor y LA HERENCIA…
     y se dejó atrás la capa entera (commit 8297d576). Efecto en la tienda, medido en
     vivo el 03-sep sobre https://soybuda.com.co:
       · la píldora decía "Paga al recibir 🇨🇴 🇲🇽" — la bandera de México de vuelta;
       · con el adicional marcado decía "TU PEDIDO: $56,900 COP MXN" — dos monedas, y
         una de ellas la que NO se cobra;
       · el renglón de detalle decía "2 Pulseras + 1 adicional" en vez de nombrar el
         Empaque Premium, que es justo el dato por el que el dueño lo pidió;
       · los precios salían "$43,000 COP" / "+$13,900.00" en vez de "$43.000" /
         "$13.900";
       · y el botón "Agregar al Carrito" había DESAPARECIDO sin que nadie lo reportara.
     Se restaura con el mismo enfoque y se le corrigen dos cosas de las que se sabía
     más que en julio (ver `unidadesPack` y el guard de `limpiarPrecios`).

     🔒 POR QUÉ ESTO NO PUEDE MOVER UN PESO. Todo lo de aquí toca TEXTO QUE SE VE, y
     nada más:
       · el bloque mexicano calcula leyendo los atributos `data-tier-*`, nunca el DOM
         renderizado, así que reescribir un nodo de texto no entra en ninguna suma;
       · el formulario colombiano tampoco lee de aquí: sus importes los pinta `money()`
         con lo que contesta /cotizar-co, y `limpiarPrecios` se salta EXPLÍCITAMENTE
         todo lo que viva bajo `[data-cco-root]`;
       · quien cobra es el servidor, y el submit sigue bloqueado hasta que conteste.

     ⚠️ POR QUÉ UN MutationObserver Y NO UN TEMPORIZADOR. `actualizarStickyTier()`
     (landing-buda.js ~624) repinta el botón en CADA cambio de pack o de adicional, y
     remata llamando a `asegurarBanderaCOD()`. Cualquier limpieza por reloj pierde esa
     carrera — de hecho la perdía: la 🇲🇽 volvía al marcar el empaque. El observador es
     lo único que garantiza que la limpieza corra DESPUÉS de cada repintado.
     Va SÍNCRONO (callback de observador = microtarea, antes del pintado siguiente) y
     no coalescido en `requestAnimationFrame`, para que no exista ni un fotograma con
     la bandera equivocada delante del cliente.
     ⚠️ Y NO SE TOCA assets/landing-buda.js: es el MISMO archivo que sirve a las dos
     tiendas de México. */

  // 🇲🇽 por punto de código, no por el carácter suelto: así el archivo se puede editar,
  // copiar o servir con cualquier codificación sin que la comparación deje de valer.
  var BANDERA_MX = String.fromCodePoint(0x1F1F2, 0x1F1FD);
  var SEL_SUB    = '[data-lb-cod-cta] .lb-atc__sub, .lb2-sticky__t2';
  var SEL_MAIN   = '.lb-atc--cod-pill .lb-atc__main, .lb2-sticky__t1';

  // Los adicionales MARCADOS, por su nombre real. El título tiene clase propia
  // (`.lb2-tickcard__title`): tomar el cuerpo entero pegaba la descripción, porque
  // `textContent` no mete saltos de línea entre hijos.
  function nombresDeExtras() {
    var out = [];
    document.querySelectorAll('.lb2-tickcard').forEach(function (card) {
      var chk = card.querySelector('input[type="checkbox"]');
      if (!chk || !chk.checked) return;
      var t = card.querySelector('.lb2-tickcard__title') ||
              card.querySelector('.lb2-tickcard__body');
      var nombre = texto(t).replace(/\s*\+?\$[\d.,]+.*$/, '').trim();
      if (nombre) out.push(nombre);
    });
    return out;
  }

  // "2 Pulseras + 1 adicional"  →  "2 Pulseras + Empaque Premium 💎"
  // (dictado del dueño: que el renglón NOMBRE lo que se agregó; "1 adicional" no le
  // dice al cliente qué marcó, que es la única razón por la que existe el renglón).
  // Idempotente por construcción: una vez reescrito el texto ya no matchea el patrón
  // "N adicional(es)", así que la pasada siguiente sale sin tocar nada — y sin eso el
  // observador se dispararía a sí mismo en bucle.
  function arreglarDetalle() {
    var det = document.querySelector('.lb2-tot__det');
    if (!det) return;
    var extras = nombresDeExtras();
    if (!extras.length) return;
    var m = texto(det).match(/^(.+?)\s*\+\s*\d+\s+adicional(?:es)?$/i);
    if (!m) return;
    var nuevo = m[1] + ' + ' + extras.join(' + ');
    if (det.textContent !== nuevo) det.textContent = nuevo;
  }


  // ── (5) LA LÍNEA 1 ES FIJA, LA 2 ES LA OFERTA (dictado del dueño 08-sep) ─────
  // El dueño invirtió la jerarquía del botón: arriba y grande el ROTULO —«PAGO
  // CONTRA ENTREGA», que es el argumento de venta de la tienda—, y debajo, chica, la
  // oferta que aplique («HOY 2X1: 2 PIEZAS POR $43.000», o el resumen del pedido si
  // marcó adicionales). Hasta hoy era al revés: la L1 la reescribía
  // `actualizarStickyTier()` (landing-buda.js) en CADA repintado con el texto del
  // pack, y la L2 llevaba el subtítulo del metafield.
  //
  // 🔒 POR QUÉ AQUÍ Y NO EN EL BOTÓN PROPIO. `.cco-btn` (cod-co.liquid) YA dice esas
  // dos líneas fijas, pero está OCULTO en la ficha (skin-landing-co.css:
  // `[data-cco-abrir]{display:none}`, decisión del dueño): lo que el cliente ve es la
  // píldora y la barra adhesiva del bloque de ofertas MEXICANO, que sirve además a las
  // dos tiendas de México y NO SE TOCA. Así que se corrige aquí, en la misma capa que
  // ya quita la 🇲🇽 y el «MXN», con cero bytes de México.
  //
  // El 🚚 NO se repite en el texto: cada botón ya trae su icono en el marcado
  // (`.lb-atc__ico` / `.lb2-sticky__ico`), y meterlo otra vez daría dos camiones.
  var L1_FIJA = 'PAGO CONTRA ENTREGA';
  // Último recurso, sólo si el botón no traía subtítulo propio. Lo normal es conservar
  // el que puso el dueño en el metafield (p. ej. «Paga al recibir 🇨🇴»), que es lo que
  // se lee mientras todavía no hay ninguna oferta calculada.
  var L2_FALLBACK = 'Paga al recibir';
  // El rótulo, para RECONOCERLO. El default del metafield es «PAGO CONTRA ENTREGA 🇲🇽»,
  // así que sin este guard la primera pasada bajaría el propio rótulo a la L2 y el botón
  // diría dos veces lo mismo, una encima de otra.
  var RE_ROTULO = /PAGO\s+CONTRA\s+ENTREGA/i;

  // ── L2 CORTA Y EN MINÚSCULAS (dictado del dueño 10-sep §8) ──────────────────
  // «Tu pedido: $72.900 · 2 collares + empaque». México compone «TU PEDIDO: $56.900»
  // + «2 Pulseras + Empaque Premium 💎», todo en mayúscula sostenida y con emoji: en
  // la segunda línea de un botón eso se lee como un segundo titular, no como apoyo.
  //
  // TRES COSAS, Y NINGUNA TOCA EL DINERO:
  //   · se baja a minúsculas y se capitaliza la primera letra (los importes no tienen
  //     caja, así que «$72.900» viaja intacto — y hay un assert que lo fija);
  //   · se quitan los emojis, porque el 🚚 del propio botón es el único permitido;
  //   · se abrevia el nombre del adicional con una lista CERRADA, una entrada por
  //     adicional real del catálogo. Nada de un diccionario abierto que mañana recorte
  //     el nombre de un producto que sí hay que nombrar entero.
  var ABREV_L2 = [[/empaque premium/g, 'empaque']];
  function acortarL2(s) {
    var t = String(s == null ? '' : s)
      .replace(/[\u{1F1E6}-\u{1F1FF}\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE0F}\u{2B00}-\u{2BFF}]/gu, '')
      .replace(/\s+/g, ' ').trim().toLowerCase();
    for (var i = 0; i < ABREV_L2.length; i++) t = t.replace(ABREV_L2[i][0], ABREV_L2[i][1]);
    t = t.replace(/\s*·\s*/g, ' · ').replace(/\s+/g, ' ').trim();
    return t ? t.charAt(0).toUpperCase() + t.slice(1) : t;
  }

  function invertirLineasCod() {
    document.querySelectorAll('[data-lb-cod-cta], [data-lb-sticky-btn]').forEach(function (btn) {
      var main = btn.querySelector('.lb-atc__main, .lb2-sticky__t1');
      var sub  = btn.querySelector('.lb-atc__sub, .lb2-sticky__t2');
      if (!main || !sub) return;
      // Con adicionales marcados, México escribe DOS spans dentro de la L1
      // (`.lb2-tot__main` = «TU PEDIDO: $56.900» y `.lb2-tot__det` = «2 Pulseras +
      // Empaque Premium 💎»). `textContent` los pegaría sin separador —
      // «$56.9002 Pulseras…» — así que se recomponen con « · ».
      var tot = main.querySelector('.lb2-tot__main');
      var det = main.querySelector('.lb2-tot__det');
      var dinamico = tot ? (texto(tot) + (det ? ' · ' + texto(det) : '')) : texto(main);
      // 🔒 LA MEMORIA ES OBLIGATORIA, no una optimización. México repinta la L1 en cada
      // cambio de pack o de adicional, pero también toca la L2 por su cuenta
      // (`asegurarBanderaCOD()` le vuelve a pegar la 🇲🇽) y entonces el paso (1) la
      // reescribe: sin recordar la oferta, esa pasada la borraría y el botón se quedaría
      // con «Paga al recibir» aunque el pack siguiera activo.
      // 🔒 SE GUARDA EL VALOR YA TRANSFORMADO, no el crudo. La comparación de abajo
      // (`texto(sub) !== l2`) es lo que hace IDEMPOTENTE a esta función, y de eso
      // depende que el MutationObserver no se despierte a sí mismo en bucle: si aquí
      // se guardara el crudo, cada pasada volvería a escribir un texto distinto del
      // que ya está puesto. El guard sigue mirando el texto CRUDO, que es donde vive
      // el rótulo por defecto del metafield («PAGO CONTRA ENTREGA 🇲🇽»).
      if (dinamico && !RE_ROTULO.test(dinamico)) btn.dataset.ccoL2 = acortarL2(dinamico);
      var l2 = btn.dataset.ccoL2 || texto(sub) || L2_FALLBACK;
      // Los dos `if` hacen la función IDEMPOTENTE, y de eso depende que el observador no
      // se despierte a sí mismo en bucle (mismo criterio que `arreglarDetalle`).
      if (texto(main) !== L1_FIJA) main.textContent = L1_FIJA;
      if (texto(sub) !== l2) sub.textContent = l2;
    });
  }

  // ── PRECIOS EN FORMATO COLOMBIANO ────────────────────────────────────────
  // El `money_format` de la tienda emite "$43,000 COP" y en algunos sitios
  // "+$13,900.00". El dueño quiere "$43.000" / "$13.900": punto de miles y sin la
  // moneda repetida (la landing entera es de una tienda en COP).
  // 🔒 Esto arregla lo que se VE. El arreglo de raíz es el formato de moneda de la
  // tienda (Configuración → Datos de la tienda), que afecta a los 75 productos y es
  // un ajuste de cuenta: no se toca sin que el dueño lo pida.
  //
  // "13,900.00" | "43,000" | "13.900"  →  "13.900"   (idempotente)
  function fmtCop(raw) {
    var s = String(raw).replace(/\s/g, '');
    // Decimales SÓLO si antes hay un grupo de miles: así "$13.900" (trece mil
    // novecientos, ya bien escrito) no se lee como "13 con 900".
    var m = s.match(/^(.*[.,]\d{3}(?:[.,]\d{3})*)[.,](\d{2})$/);
    if (m) s = m[1];
    var entero = s.replace(/[.,]/g, '');
    if (!/^\d+$/.test(entero)) return null;
    // Menos de 3 dígitos no puede llevar separador de miles, y reformatear ahí sólo
    // sirve para estropear un número que no era un precio ("$1.5" → "15").
    if (entero.length < 3) return null;
    return entero.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  var CONTENEDORES = [
    '.lb-section', '.lb2-ticks', '.lb-atc--cod-pill', '.lb2-sticky',
    '.price', '.price__container', '.lb2-tot__main'
  ].join(',');

  /* ── (4b) LOS ATRIBUTOS `-money` DE LA ESCALERA — LA RAÍZ DE F5 ─────────────
     🐛 EL DEFECTO, medido en la tienda viva el 10-sep: en la ficha del 2x1 la TERCERA
     tarjeta («3 o más con el 55% OFF») enseñaba «$93,150 COP» y su tachado «$207,000
     COP» mientras las otras dos decían «$55.200», «$69.000» y «$138.000». Y el motivo de
     que fuera SIEMPRE la tercera: es el tier ABIERTO, el único cuyo precio se REESCRIBE
     en tiempo de ejecución. `recomputarAbierto` (landing-buda.js:536-565, México, no se
     toca) deriva el patrón de formato del ATRIBUTO `data-tier-precio-money` —que
     conserva el `money_format` de la tienda— y con él vuelve a pintar `.lb-tier__now`.
     Así que la limpieza de texto de abajo no era un arreglo: era una REPARACIÓN que
     tenía que ganarle la carrera al repintado. Reproducido: al pulsar el «+» del stepper
     el nodo dice «$124,200 COP» en la MISMA tarea, y sólo vuelve a «$124.200» cuando el
     MutationObserver despierta (una microtarea después). El cliente ve el parpadeo y
     cualquiera que mire el DOM en ese instante ve el precio con el formato equivocado.

     Se arregla en la FUENTE: se normalizan los dos atributos, así `patronMoneda` deriva
     el patrón de la casa («$» + punto de miles + cero decimales) y CADA reescritura sale
     ya bien. La limpieza de texto se queda como cinturón.

     🔒 POR QUÉ ESTO NO MUEVE UN PESO. El atributo es un STRING DE PRESENTACIÓN: el
     importe se recalcula siempre desde `data-tier-precio` / `data-tier-comp`, que son
     números crudos y no se tocan. Y los tres consumidores del string aguantan el formato
     nuevo: dos lo PINTAN (`.lb-tier__now`, el sticky) y el tercero lo parsea con
     `precioNumerico`, que trata «93.150» como entero de miles porque tras el separador
     quedan TRES dígitos (landing-buda.js: sólo es decimal con 1-2) → 93150, verificado.
     Ninguno lo manda al servidor.

     ⚠️ No despierta al observador: `observe` mira `childList`/`subtree`/`characterData`,
     nunca `attributes` — así que esto no puede realimentarse. Y es idempotente: una vez
     normalizado, `fmtCop` devuelve el mismo string. */
  var SEL_MONEY_ATTR = ['data-tier-precio-money', 'data-tier-comp-money'];
  function limpiarAtributosMoney() {
    document.querySelectorAll('.lb-tier').forEach(function (t) {
      SEL_MONEY_ATTR.forEach(function (a) {
        var v = t.getAttribute(a);
        if (!v || !/\d/.test(v)) return;
        var nuevo = String(v)
          .replace(/\$\s*(\d[\d.,]*)(\s*COP\b)?/g, function (todo, num) {
            var f = fmtCop(num); return f == null ? todo : '$' + f;
          })
          .replace(/(?:^|\s)(\d[\d.,]*)\s*COP\b/g, function (todo, num) {
            var f = fmtCop(num); return f == null ? todo : (/^\s/.test(todo) ? ' ' : '') + '$' + f;
          });
        if (nuevo !== v) t.setAttribute(a, nuevo);
      });
    });
  }

  function limpiarPrecios() {
    document.querySelectorAll(CONTENEDORES).forEach(function (cont) {
      // 🔒 EL FORMULARIO COLOMBIANO NO SE TOCA. Sus importes ya vienen del servidor y
      // los pinta `money()` de cod-co.js con el formato correcto; pasarle un
      // reescritor de texto por encima al único sitio donde el cliente lee lo que va a
      // pagar es exactamente el riesgo que este carril tiene prohibido correr.
      if (cont.closest && cont.closest('[data-cco-root]')) return;
      var w = document.createTreeWalker(cont, NodeFilter.SHOW_TEXT, null);
      var nodos = [], n0;
      while ((n0 = w.nextNode())) nodos.push(n0);
      nodos.forEach(function (n) {
        if (n.parentNode && n.parentNode.closest && n.parentNode.closest('[data-cco-root]')) return;
        var t = n.nodeValue;
        if (!t || !/\d/.test(t)) return;
        var nuevo = t
          // con símbolo: "$43,000 COP" / "+$13,900.00"
          .replace(/\$\s*(\d[\d.,]*)(\s*COP\b)?/g, function (todo, num) {
            var f = fmtCop(num); return f == null ? todo : '$' + f;
          })
          // sin símbolo: el precio NATIVO del tema dice "43,000 COP"
          .replace(/(?:^|\s)(\d[\d.,]*)\s*COP\b/g, function (todo, num) {
            var f = fmtCop(num); return f == null ? todo : (/^\s/.test(todo) ? ' ' : '') + '$' + f;
          });
        if (nuevo !== t) n.nodeValue = nuevo;
      });
    });
  }

  function limpiar() {
    // (1) LA BANDERA DE MÉXICO, que `asegurarBanderaCOD()` re-inyecta en cada
    //     repintado. Se quita SÓLO la mexicana: la 🇨🇴 que va al lado es la buena y se
    //     queda. La sublínea es texto plano (verificado en vivo: innerHTML ===
    //     "Paga al recibir 🇨🇴 🇲🇽"), así que asignar `textContent` no destruye hijos.
    document.querySelectorAll(SEL_SUB).forEach(function (el) {
      var t = el.textContent || '';
      if (t.indexOf(BANDERA_MX) < 0) return;
      el.textContent = t.split(BANDERA_MX).join('').replace(/\s+/g, ' ').trim();
    });
    // (2) EL "MXN" del total con adicionales (landing-buda.js ~671). El importe ya
    //     trae la moneda del `money_format` de la tienda, así que ese "MXN" sobra Y
    //     MIENTE: en Colombia se cobra en pesos colombianos.
    document.querySelectorAll('.lb2-tot__cur').forEach(function (el) {
      if (/MXN/i.test(el.textContent || '')) el.remove();
    });
    document.querySelectorAll(SEL_MAIN).forEach(function (el) {
      if (/\bMXN\b/.test(el.textContent || '')) {
        el.innerHTML = el.innerHTML.replace(/\s*\bMXN\b/g, '');
      }
    });
    // (3) el detalle con el nombre real del adicional
    arreglarDetalle();
    // (4) los precios sin "COP" y con punto de miles
    // 🔴 LOS ATRIBUTOS **ANTES** QUE EL TEXTO, y el orden es load-bearing: si el texto se
    //     limpiara primero, el repintado del tier abierto que dispare esta misma pasada
    //     volvería a escribirlo con el patrón viejo del atributo. Normalizada la fuente,
    //     la limpieza de texto sólo tiene que rematar lo que ya venía servido de Liquid.
    limpiarAtributosMoney();
    limpiarPrecios();
    // (5) Y AL FINAL, la jerarquía del botón. EL ORDEN NO ES ESTÉTICO: esta tarea COPIA
    //     el texto de la L1 a la L2, así que tiene que leerlo YA LIMPIO — sin la 🇲🇽 del
    //     paso (1), sin el «MXN» del (2), con el adicional nombrado por el (3) y con los
    //     precios en formato colombiano del (4). Si corriera antes, la L2 heredaría
    //     justo lo que las cuatro tareas anteriores existen para quitar.
    invertirLineasCod();
  }

  // ── BOTÓN "AGREGAR AL CARRITO" DEBAJO DE LA PÍLDORA ──────────────────────
  // Se inyecta por JS porque la píldora la pinta el snippet de ofertas, DENTRO del
  // bloque: no hay forma de colocar un bloque del tema ahí en medio.
  // ⚠️ Este botón lleva al carrito de Shopify, que cobra el precio de la VARIANTE —
  // no el del pack. Es la vía "compra normal"; la del pack con su descuento es la
  // píldora, que abre el formulario COD. Son dos caminos distintos a propósito.
  // 🐛 CORREGIDO EN LA RESTAURACIÓN (03-sep) — LO CAZÓ LA REVISIÓN ADVERSARIA.
  // La versión de julio buscaba `[data-lb-variant-id], select[name="id"]`. Los DOS
  // selectores están MUERTOS contra el marcado real: `data-lb-variant-id` tiene CERO
  // apariciones en todo src/landing/theme-assets (y cero en el landing-buda.js que
  // sirve soybuda.com.co), y el nodo de verdad es un
  // `<input type="hidden" name="id">` (landing-buda-offers-inline.liquid:203), que no
  // matchea `select[name="id"]`. O sea que la función caía SIEMPRE al último recurso —
  // `variantes[0]`, la primera variante de Shopify — y se saltaba justo el único sitio
  // donde vive lo que el cliente eligió: `syncVariant()` escribe ahí su elección
  // (landing-buda.js:302, `if (v && idInput) idInput.value = v.id`).
  // Reproducido en Chrome sin cabeza con un producto colombiano real
  // (anillo-nudo-de-bruja): el cliente elige DORADO y el POST sale con la PLATEADA.
  // Hay 19 productos multivariante en el catálogo colombiano.
  // Es el mismo defecto que México ya arregló dos veces — el propio snippet trae la
  // corrección del dueño del 20-jul ("el selector arrancaba en Plateado mientras el
  // hero muestra la dorada") y landing-buda.js:836 lo remata: "en contra entrega eso se
  // paga en la puerta".
  // Ahora se lee EL MISMO nodo que leen `initBuyBox`/`initCod` (el snippet lo declara
  // como contrato en su línea 525), y el `variantes[0]` queda donde debe: de último
  // recurso, para la landing de un producto de una sola variante.
  function variantePack() {
    var f = document.querySelector('[data-lb-form]');
    var input = f ? f.querySelector('input[name="id"]') : null;
    if (input && input.value) return String(input.value);
    var h = document.querySelector('[data-lb-variants]');
    if (h) {
      try { var v = JSON.parse(h.textContent || '[]'); if (v[0] && v[0].id) return String(v[0].id); } catch (e) {}
    }
    return null;
  }

  // 🐛 CORREGIDO EN LA RESTAURACIÓN. La versión de julio leía SÓLO `data-tier-n`, que
  // el tier ABIERTO ("3 o más") no tiene → caía al fallback y agregaba UNA unidad
  // aunque el cliente hubiera subido el stepper a cuatro. Se lee primero
  // `data-tier-unidades` (las unidades EFECTIVAS del tier abierto), que es la misma
  // fuente que usa la herencia de arriba.
  function unidadesPack() {
    var act = document.querySelector('.lb-tier.is-active');
    if (!act) return 1;
    var n = parseInt(act.getAttribute('data-tier-unidades') || '', 10);
    if (!isFinite(n) || n <= 0) n = parseInt(act.getAttribute('data-tier-n') || '1', 10);
    return (isFinite(n) && n > 0) ? n : 1;
  }

  /* ── UNA LÍNEA POR PIEZA ELEGIDA (dictado del dueño 09-sep) ────────────────
     «Si escojo dama y caballero y lo agrego al carrito, solamente me pone de caballero.
      Necesito que en el carrito pongas las dos referencias diferentes, porque si no
      manda mal el pedido.»

     🐛 Y era exactamente eso: el botón agregaba UNA variante —la de la píldora oculta—
     con `quantity = unidades`. Las elecciones por unidad se pintaban, se mandaban bien
     por NUESTRO formulario… y se perdían enteras al pasar por el carrito de Shopify.
     El cliente pedía un Dama y un Caballero y le llegaban dos Caballero.

     Se construye una línea por VARIANTE distinta, con su cantidad. El id sale de
     `[data-lb-variants]` (el JSON del tema, que sí trae ids) emparejando por los VALORES
     DE OPCIÓN de `[data-cco-variantes]`, no por posición: los dos JSON los emiten
     bloques distintos y dar por hecho que van en el mismo orden es cómo se despacha la
     pieza equivocada sin que nadie se entere.

     🔒 SI ALGO NO EMPAREJA, DEVUELVE null Y SE USA EL CAMINO DE SIEMPRE. Perder la
     precisión de las piezas es malo; dejar al cliente sin poder agregar al carrito es
     peor. */
  function lineasDelPack() {
    var V = variantes();
    if (!V.length) return null;
    var n = document.querySelector('[data-lb-variants]');
    if (!n) return null;
    var tema;
    try { tema = JSON.parse(n.textContent || '[]'); } catch (e) { return null; }
    if (!Array.isArray(tema) || !tema.length) return null;

    // clave canónica = los valores de opción en orden, en minúsculas y sin espacios de
    // sobra. Es la misma idea que `resolverVarianteCo` del servidor.
    var clave = function (vals) {
      return (vals || []).map(function (x) {
        return String(x == null ? '' : x).trim().toLowerCase();
      }).join('||');
    };
    var porClave = {};
    for (var t = 0; t < tema.length; t++) {
      if (!tema[t] || tema[t].id == null) continue;
      porClave[clave(tema[t].options)] = String(tema[t].id);
    }

    var unidades = unidadesPack();
    var cuenta = {};
    var orden = [];
    for (var i = 0; i < unidades; i++) {
      var j = eleccionDe(i);
      var v = V[j];
      if (!v) return null;
      var id = porClave[clave((v.optionValues || []).map(function (o) { return o && o.name; }))];
      if (!id) return null;              // no se pudo emparejar: fuera, al camino viejo
      if (!cuenta[id]) { cuenta[id] = 0; orden.push(id); }
      cuenta[id] += 1;
    }
    if (!orden.length) return null;
    return orden.map(function (id) { return { id: id, quantity: cuenta[id] }; });
  }

  function montarAtc() {
    var pill = document.querySelector('.lb-atc--cod-pill');
    if (!pill || document.querySelector('.cco-atc')) return;
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'cco-atc';
    // UN SOLO EMOJI EN LA PAREJA (dueño 10-sep §8): el 🚚 del botón de contra entrega.
    // El 🛒 de aquí y el 💎 de la L2 se van; sin icono propio, y a propósito — el dueño
    // dejó el SVG de línea como opcional y «más fino» es sin él. La jerarquía la marca
    // el camión del otro botón, que es el que tiene que ganar.
    b.textContent = 'Agregar al carrito';
    b.addEventListener('click', function () {
      var lineas = null;
      try { lineas = lineasDelPack(); } catch (e) { lineas = null; }
      var id = lineas ? null : variantePack();
      if (!lineas && !id) return;
      b.disabled = true;
      var peticion;
      if (lineas) {
        peticion = fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: lineas }),
        });
      } else {
        var fd = new FormData();
        fd.append('id', id);
        fd.append('quantity', String(unidadesPack()));
        peticion = fetch('/cart/add.js', { method: 'POST', body: fd, credentials: 'same-origin' });
      }
      peticion
        .then(function (r) { return r.ok ? r.json() : Promise.reject(r); })
        .then(function () {
          b.classList.add('is-ok');
          b.textContent = 'Agregado al carrito ✓';
          setTimeout(function () { window.location.href = '/cart'; }, 700);
        })
        .catch(function () {
          b.disabled = false;
          b.textContent = 'No se pudo agregar · reintentar';
        });
    });
    pill.parentNode.insertBefore(b, pill.nextSibling);
  }

  /* ── LOS DOS BOTONES MIDEN LO MISMO SIEMPRE (F2 de la verificacion del 10-sep) ──
     EL DEFECTO MEDIDO: a 320 y a 768 el de contra entrega media 96 px y el de carrito
     60. La receta comun de skin-landing-co.css les pone a los dos el mismo suelo
     (`min-height: 60px`), y con eso bastaba en el movil de referencia; pero la píldora
     lleva DOS lineas y la segunda es CONTENIDO —«Tu pedido: $72.900 · 2 collares +
     empaque»—, asi que en una columna estrecha se parte en dos renglones y la píldora
     crece. El de carrito no tiene por que crecer solo: se queda en 60 y la pareja que el
     dueno pidio identica deja de medir lo mismo.

     ASI QUE SE MIDE Y SE FIJA, que es la unica forma de que sea verdad con CUALQUIER
     texto y en CUALQUIER ancho. El CSS pone la letra (tokens `--cco-btn-*`, que hacen
     que el rotulo quepa en una linea); esto pone el alto. Un reparto, no dos verdades.

     🔒 `offsetHeight` Y NO `getBoundingClientRect()`. Mexico le pone a la píldora
        `animation: lb2-cta-shake` con `scale: 1.06`: el rect devuelve la caja YA ESCALADA
        (63,5 px cuando el layout son 60) y le pasariamos al hermano un alto inventado que
        ademas late. `offsetHeight` ignora las transformaciones.
     🔒 SOLO SI NUESTRA RECETA ESTA PUESTA. `--cco-par-botones` la declara
        skin-landing-co.css sobre `.cco-atc`. Si esa hoja no cargo, aqui no se finge una
        pareja que no existe: el boton se queda como nazca (y la contraprueba de la suite
        —«sin el skin NO miden lo mismo»— sigue midiendo el defecto de verdad).
     🔒 NO HACE FALTA UN `Math.max(60, ...)`: con la receta puesta la píldora tiene ella
        misma `min-height: 60px`, asi que su `offsetHeight` nunca baja del suelo comun. El
        piso vive en el CSS y aqui no se copia — un 60 escrito tambien aqui seria el
        segundo sitio donde cambiarlo el dia que el dueno lo mueva.
     🔒 ES IDEMPOTENTE (no escribe si ya vale lo mismo) y escribe un ATRIBUTO. El
        MutationObserver de este archivo observa `childList` y `characterData`, NO
        `attributes`, asi que esto no puede despertarse a si mismo; el guard de igualdad
        esta igual, porque `tick()` corre en cada mutacion y una escritura por mutacion
        seria trabajo tirado.
     ⚠️ Cero red, cero temporizadores: una lectura de layout y, como mucho, una escritura. */
  /* ⚡ UNA MEDICIÓN POR FOTOGRAMA, NO UNA POR MUTACIÓN (velocidad, 21-oct-2026).
     Medido en la tienda (móvil lento, CPU ×4): esta función se llevaba 144–191 ms de los
     172–220 ms de CPU del puente. `tick()` corre en CADA mutación de la página —y las apps
     (reseñas, Addi, banners) mutan cientos de veces al cargar— y aquí se lee `offsetHeight`
     justo después de que las demás funciones del tick escribieron el DOM: cada lectura
     obligaba al navegador a recalcular el diseño de la página ENTERA en ese momento.
     Ahora `tick()` la sigue llamando igual, pero la medición se agenda en
     `requestAnimationFrame` y se junta: como mucho UNA por fotograma, y siempre ANTES de
     pintar, así que el cliente nunca ve un fotograma con los dos botones desiguales que no
     viera ya antes. Sin `requestAnimationFrame` (navegador muy viejo) se mide al momento,
     como siempre. */
  var altoAgendado = false;
  function igualarAltoBotones() {
    if (typeof window.requestAnimationFrame !== 'function') { medirYFijarAlto(); return; }
    if (altoAgendado) return;
    altoAgendado = true;
    window.requestAnimationFrame(function () {
      altoAgendado = false;
      try { medirYFijarAlto(); } catch (e) { /* la pareja se queda como estaba */ }
    });
  }
  function medirYFijarAlto() {
    var pill = document.querySelector('.lb-atc--cod-pill');
    var atc = document.querySelector('.cco-atc');
    if (!pill || !atc) return;
    var cs = window.getComputedStyle ? window.getComputedStyle(atc) : null;
    if (!cs || String(cs.getPropertyValue('--cco-par-botones') || '').trim() !== '1') return;
    var alto = pill.offsetHeight;
    if (!alto) return;                     // píldora oculta: no hay nada que igualar
    var puesto = parseFloat(atc.style.minHeight);
    if (puesto === alto) return;
    atc.style.minHeight = alto + 'px';
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     (3) LOS CHIPS DE VARIANTE — UNA ELECCIÓN POR UNIDAD (dictado del dueño 09-sep)
     ═══════════════════════════════════════════════════════════════════════════
     LO QUE VIO EL DUEÑO en la ficha de la Pulsera 7 Chakras: arriba de todo un
     selector suelto «TAMAÑO · Dama | Caballero», y más abajo tres tarjetas de oferta
     —una de ellas el 2x1— sin ninguna forma de decir QUÉ dos piezas quiere. En un 2x1
     de un producto con tallas eso no es un detalle de piel: el cliente elige un pack de
     dos y sólo puede votar UNA variante.

     LO QUE SE HACE: cada tarjeta de oferta lleva, bajo su título, una fila por UNIDAD
     («Unidad 1: [Dama] [Caballero]»), y el formulario colombiano lleva exactamente los
     mismos chips. Es UNA sola selección vista en dos sitios, no dos.

     ═══════════════════════════════════════════════════════════════════════════
     🔒 LAS TRES REGLAS QUE MANTIENEN ESTO HONESTO
     ═══════════════════════════════════════════════════════════════════════════
     (1) NO SE INVENTA UNA VARIANTE. Los chips salen de `[data-cco-variantes]`, el
         MISMO JSON que ya emite snippets/cod-co.liquid desde las variantes REALES del
         producto y que cod-co.js usa para armar `optionValues`. Aquí no se lee ni el
         metafield ni el catálogo por segunda vez.
     (2) NO SE TOCA EL BLOQUE MEXICANO. `landing-buda-offers-inline.liquid` sirve a las
         dos tiendas de México y no se edita: los chips se INYECTAN dentro de la tarjeta
         por JS, igual que el botón «Agregar al Carrito» de la sección (2).
     (3) SE APLICA POR CLICS, como todo lo demás de este archivo. Al abrir el pedido no
         se escribe en las variables de cod-co.js: se PULSA el chip que corresponde, que
         es lo que dispara su repintado y su recotización.

     ⚠️ Y NO PUEDE MOVER UN PESO: la variante no entra en ninguna suma —el precio lo
     dicen los tiers y lo cierra /cotizar-co—, y lo que viaja al servidor sigue siendo
     `optionValues`, resuelto contra las variantes reales por `resolverVarianteCo`.

     ⚠️ EL SELECTOR DE ARRIBA SE OCULTA, PERO SÓLO SI ESTOS CHIPS SE MONTARON. La clase
     `cco-varchips` la pone este código en <html> cuando de verdad pintó chips, y es de
     ella de la que cuelga la regla que esconde `.lb-variant` (skin-landing-co.css). Si
     algo aquí falla, el selector viejo se queda visible y el cliente sigue pudiendo
     elegir: nunca una ficha con variantes y sin forma de elegirlas.

     ⚠️ TOPE DE FILAS. El pack abierto llega a 10 unidades y diez filas de chips
     convierten la tarjeta en un formulario. Se pintan hasta MAX_FILAS y el resto se
     dice en voz alta («+N se eligen al finalizar»); en el formulario COD sí salen
     TODAS, que es donde se cierra el pedido. No hay divergencia posible porque la
     selección es UN SOLO array: las unidades que no se pintan aquí conservan su valor
     por defecto, el mismo que tendrían sin este cambio. */

  var MAX_FILAS = 4;
  var ELECCIONES = [];   // índice en VARIANTES por unidad — el MISMO contrato que cod-co.js
  var VARS = null;       // caché del JSON de variantes (se lee una vez)
  var aplicando = false; // guard de re-entrada de la sincronización por clics

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // Las variantes REALES, del mismo JSON que lee cod-co.js. Sin `[data-cco-root]` en la
  // página (plantilla sin formulario colombiano) esto devuelve [] y toda la sección
  // queda en NO-OP: la landing se comporta exactamente como hoy.
  function variantes() {
    if (VARS) return VARS;
    var n = document.querySelector('[data-cco-variantes]');
    if (!n) return [];
    try {
      var v = JSON.parse(n.textContent || '[]');
      if (Array.isArray(v)) { VARS = v; return v; }
    } catch (e) {}
    return [];
  }

  // Primera variante DISPONIBLE. Misma regla que cod-co.js (`varPorDefecto`): ofrecer
  // por defecto una agotada termina en un pedido que no se puede surtir.
  // 🔗 `?variant=<id>` (dueño 02-oct): si la URL trae el id de una variante de ESTE producto y está
  // DISPONIBLE, manda ella —los anuncios de Meta enlazan a la variante que muestran—; si no, la
  // primera disponible como siempre. Misma regla y mismo nombre que cod-co.js (`varianteDeLaUrl`),
  // y se lee UNA vez al cargar porque Dawn reescribe `?variant=` en cada cambio de selector.
  var VARIANTE_URL = -2;   // -2 = sin leer · -1 = ninguna · >= 0 = índice en variantes()
  function varianteDeLaUrl() {
    if (VARIANTE_URL !== -2) return VARIANTE_URL;
    var V = variantes();
    if (!V.length) return -1;   // sin variantes aún: no se memoiza, se vuelve a intentar
    VARIANTE_URL = -1;
    try {
      var m = String(window.location.search || '').match(/[?&]variant=(\d+)(?:&|#|$)/);
      if (m) {
        for (var k = 0; k < V.length; k++) {
          if (V[k] && String(V[k].id) === m[1] && V[k].disponible !== false) { VARIANTE_URL = k; break; }
        }
      }
    } catch (e) { VARIANTE_URL = -1; }
    return VARIANTE_URL;
  }
  function porDefecto() {
    var V = variantes();
    var u = varianteDeLaUrl();
    if (u >= 0) return u;
    for (var k = 0; k < V.length; k++) { if (V[k].disponible !== false) return k; }
    return 0;
  }
  function eleccionDe(i) {
    var j = ELECCIONES[i];
    return (j >= 0 && j < variantes().length) ? j : porDefecto();
  }

  // Unidades que representa una tarjeta. `data-tier-unidades` lo mantiene landing-buda.js
  // al mover el ± del pack abierto; `data-tier-n` es el mínimo del pack. Misma lectura y
  // mismo orden que `leerLanding()` — si divergieran, la tarjeta pintaría un número de
  // filas y el pedido llevaría otro.
  function unidadesDeTarjeta(card) {
    var u = parseInt(card.getAttribute('data-tier-unidades'), 10);
    if (!(u > 0)) u = parseInt(card.getAttribute('data-tier-n'), 10);
    return (u > 0) ? u : 1;
  }

  // Dónde entra el bloque de chips DENTRO de la tarjeta: bajo el título y el precio
  // (o sea antes de la fila «Ahorras…»), y siempre por encima del stepper, que tiene
  // que seguir siendo lo último. Si la tarjeta no trae ninguno de los dos, al final.
  function insertarEn(card, caja) {
    var ref = card.querySelector('.lb-tier__meta') || card.querySelector('.lb-stepper');
    if (ref) card.insertBefore(caja, ref); else card.appendChild(caja);
  }

  /* ── SIEMPRE QUE HAYA QUE ELEGIR, DESPLEGABLE (dictado del dueño 09-sep) ────
     Primero en 4, y el dueño lo pidió también para la Alineación 7 Chakras, el
     Protección x4 y los dos anillos, que son de DOS opciones. Con una sola variante no
     hay nada que elegir y no se pinta nada; con dos o más, desplegable.

     🔒 EL MISMO UMBRAL QUE EL FORMULARIO (cod-co.js), con un assert que lo fija.
     🔒 SÓLO CAMBIA LA PIEL: la elección sigue siendo `ELECCIONES[]` y sigue viajando por
     el mismo camino (pintar → miniaturas → sincronizar → aplicar al formulario). */
  var UMBRAL_DESPLEGABLE = 2;
  function usarDesplegable() { return variantes().length >= UMBRAL_DESPLEGABLE; }

  function htmlChips(filas, unidades) {
    var V = variantes();
    var h = '';
    for (var i = 0; i < filas; i++) {
      var elegido = eleccionDe(i);
      if (usarDesplegable()) {
        h += '<label class="ccl-uni ccl-uni--sel"><span class="ccl-uni__lbl">Unidad ' + (i + 1) + '</span>' +
             '<select class="ccl-uni__sel" data-ccl-sel="' + i + '" aria-label="Unidad ' + (i + 1) + '">';
        for (var k = 0; k < V.length; k++) {
          h += '<option value="' + k + '"' + (k === elegido ? ' selected' : '') +
               (V[k].disponible === false ? ' disabled' : '') + '>' +
               esc(V[k].titulo) + (V[k].disponible === false ? ' (agotada)' : '') + '</option>';
        }
        h += '</select></label>';
        continue;
      }
      h += '<span class="ccl-uni"><span class="ccl-uni__lbl">Unidad ' + (i + 1) + '</span>' +
           '<span class="ccl-chips">';
      for (var j = 0; j < V.length; j++) {
        var agotada = V[j].disponible === false;
        var on = (j === elegido);
        h += '<button type="button" class="ccl-chip' + (on ? ' is-on' : '') + '"' +
             ' data-ccl-chip="' + i + '" data-ccl-idx="' + j + '"' +
             ' aria-pressed="' + (on ? 'true' : 'false') + '"' +
             (agotada ? ' disabled' : '') + '>' +
             esc(V[j].titulo) + (agotada ? ' (agotada)' : '') + '</button>';
      }
      h += '</span></span>';
    }
    if (unidades > filas) {
      h += '<span class="ccl-vars__mas">+' + (unidades - filas) +
           ' se eligen al finalizar el pedido</span>';
    }
    return h;
  }

  // Firma de lo pintado. Sin ella, cada escritura despertaría al MutationObserver, que
  // volvería a llamar aquí, que volvería a escribir: bucle. Con ella la segunda pasada
  // no toca nada y el observador se calla.
  function firmaDe(filas, unidades) {
    var f = filas + '|' + unidades;
    for (var i = 0; i < filas; i++) f += '|' + eleccionDe(i);
    return f;
  }

  function pintarChips() {
    var V = variantes();
    // Un producto de UNA sola variante no tiene nada que elegir: ni chips, ni ocultar el
    // selector de arriba (que en ese caso el snippet mexicano ni siquiera pinta).
    if (V.length < 2) return false;
    var cards = document.querySelectorAll('.lb-tier');
    if (!cards.length) return false;

    // ── SÓLO EN LA TARJETA MARCADA (dictado del dueño 09-sep) ───────────────
    // Antes se pintaban en las TRES, y eso convertía el bloque de ofertas en un
    // formulario: nueve filas de chips compitiendo con los precios, y el cliente
    // eligiendo la talla de dos packs que no va a comprar. Ahora la elección vive donde
    // vive la decisión — la tarjeta marcada— igual que ya quedó en el formulario COD.
    //
    // 🔒 MISMA CADENA DE FALLBACK QUE `leerLanding` (is-active → popular → la primera).
    // Si divergieran, los chips se pintarían en una tarjeta y el pedido se abriría en
    // otra. Y con la cadena entera vacía no se monta nada: sin `cco-varchips` el
    // selector viejo se queda visible y el cliente sigue pudiendo elegir.
    var marcada = document.querySelector('.lb-tier.is-active') ||
                  document.querySelector('.lb-tier[data-tier-popular="true"]') ||
                  cards[0];

    var montado = false;
    for (var c = 0; c < cards.length; c++) {
      var card = cards[c];
      var caja = card.querySelector('[data-ccl-vars]');
      // LAS DEMÁS SE LIMPIAN. No basta con no pintarlas: al cambiar de oferta, la caja
      // de la tarjeta anterior ya existe en el DOM y se quedaría ahí para siempre (3
      // asserts en rojo si se quita esta línea).
      // ⚠️ La firma se borra por HIGIENE, no porque haga falta: medido por mutación,
      // dejarla no rompe nada, porque el guard de "al día" exige ADEMÁS que la caja
      // exista y aquí se acaba de quitar. Se conserva para que no queden atributos
      // mintiendo sobre un contenido que ya no está — pero no se presenta como candado.
      if (card !== marcada) {
        if (caja) caja.parentNode.removeChild(caja);
        card.removeAttribute('data-ccl-firma');
        continue;
      }
      var unidades = unidadesDeTarjeta(card);
      var filas = Math.min(unidades, MAX_FILAS);
      var firma = firmaDe(filas, unidades);
      if (caja && card.getAttribute('data-ccl-firma') === firma) { montado = true; continue; }
      if (!caja) {
        caja = document.createElement('span');
        caja.className = 'ccl-vars';
        caja.setAttribute('data-ccl-vars', '');
        insertarEn(card, caja);
      }
      // Lo elegido NO se pierde al cambiar de tarjeta: `ELECCIONES` es del módulo y
      // `htmlChips` lo lee por `eleccionDe`; las unidades nuevas nacen en la primera
      // variante disponible, igual que en el formulario.
      caja.innerHTML = htmlChips(filas, unidades);
      card.setAttribute('data-ccl-firma', firma);
      montado = true;
    }
    // La clase que autoriza a esconder el selector viejo. Se pone SÓLO si de verdad hay
    // chips en pantalla (ver la nota de arriba).
    if (montado) raiz.classList.add('cco-varchips');
    return montado;
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     (4) LA LETRA PEQUEÑA DEL 2x1 EN LA FICHA (dictado del dueño 09-sep)
     ═══════════════════════════════════════════════════════════════════════════
     El formulario colombiano ya la lleva (`.cco-nota-2x1`, cod-co.js), pero el cliente
     que elige la oferta en la FICHA no la ve hasta que abre el pedido. Va debajo de las
     tres tarjetas de «OFERTA DEL DÍA» y por encima de los sellos de confianza — o sea al
     cerrar el bloque donde se elige, que es donde se lee.

     🔒 EL CRITERIO ES EL MISMO Y LA FUENTE TAMBIÉN. Se lee `[data-cco-landing]`, el
     MISMO JSON del metafield que usa cod-co.js, y se aplica el MISMO patrón sobre los
     MISMOS dos campos (`titulo` y `badge`) del tier de n === 2. No se mira el texto que
     hay pintado en la tarjeta: el bloque mexicano lo escribe con emojis y saltos, y
     depender de él pondría un tercer criterio en la casa. Si el metafield no está, no
     hay nota: FAIL-CLOSED — anunciar la restricción de una promoción que no existe
     insinúa que existe.

     ⚠️ EL PATRÓN ESTÁ ESCRITO DOS VECES (aquí y en cod-co.js) porque son dos archivos
     que el navegador carga por separado y ninguno importa al otro. Los dos literales los
     fija un assert (scripts/test-cod-co-tema.mjs): sin él, el día que alguien amplíe el
     regex canónico —un «3x2», por ejemplo— la nota dejaría de salir EN SILENCIO en uno
     de los dos sitios y el cliente leería una restricción distinta en cada pantalla.

     ⚠️ SE INYECTA POR JS, como todo lo de este archivo: el bloque mexicano
     (landing-buda-offers-inline.liquid) sirve a las dos tiendas de México y no se toca. */

  var DOSXUNO_RE_CO = /2\s*[x×]\s*1|2\s*por\s*1|compra\s*1\s*(?:y\s*)?lleva\s*2|lleva\s*2\s*(?:y\s*)?paga\s*1/i;
  var LAND2X1 = null;   // caché del veredicto (el metafield no cambia dentro de la página)

  function hay2x1() {
    if (LAND2X1 !== null) return LAND2X1;
    LAND2X1 = false;
    var n = document.querySelector('[data-cco-landing]');
    if (!n) return LAND2X1;   // plantilla sin formulario colombiano → nada que anunciar
    try {
      var d = JSON.parse(n.textContent || 'null');
      var tiers = (d && d.packs && Array.isArray(d.packs.tiers)) ? d.packs.tiers : [];
      for (var i = 0; i < tiers.length; i++) {
        var t = tiers[i];
        if (!t || typeof t !== 'object') continue;
        // Mismo `n` normalizado que `leerTiers` (n ?? unidades) y misma prueba CAMPO POR
        // CAMPO que `esTier2x1Co`: sobre los dos textos concatenados, un «compra 1» al
        // final del título con un «lleva 2» al principio del badge casaría a caballo del
        // corte y se inventaría un 2x1 que nadie escribió.
        if (Math.trunc(Number(t.n != null ? t.n : t.unidades)) !== 2) continue;
        if (DOSXUNO_RE_CO.test(String(t.titulo || '')) || DOSXUNO_RE_CO.test(String(t.badge || ''))) {
          LAND2X1 = true;
          break;
        }
      }
    } catch (e) {}
    return LAND2X1;
  }

  // Idempotente: si ya está puesta no se vuelve a escribir (si no, cada escritura
  // despertaría al MutationObserver, que volvería a llamar aquí: bucle).
  function pintarNota2x1() {
    if (!hay2x1()) return false;
    // El contenedor de las tarjetas. `.lb-packs` es el que las envuelve en las dos
    // plantillas del bloque; sin él no hay «debajo de las tarjetas» que respetar.
    var caja = document.querySelector('.lb-packs');
    if (!caja) return false;
    if (caja.querySelector('[data-ccl-nota2x1]')) return false;
    // Sólo con tarjetas de verdad: en una página a medio pintar, una nota suelta sin
    // ofertas encima no se refiere a nada.
    if (!caja.querySelector('.lb-tier')) return false;
    var p = document.createElement('p');
    p.className = 'ccl-nota-2x1';
    p.setAttribute('data-ccl-nota2x1', '');
    /* 🔴 EL ESTILO VA EN LÍNEA, Y NO ES UN ATAJO. `skin-landing-co.css` NO es un asset:
       viaja inline dentro del bloque `lb_skin_co` de templates/product.cod-co.json, y esa
       plantilla no la sube ningún script —`desplegar-tema-co.mjs` sólo la COMPARA y avisa,
       porque reescribir la plantilla del producto es otra clase de riesgo y la edita una
       persona en el editor de temas—. Poner la regla allí dejaría la nota DESPLEGADA A
       MEDIAS: el <p> saldría en la ficha con la tipografía del tema (grande, alineada a la
       izquierda, en la tinta del cuerpo) hasta que alguien copiara la hoja a mano. Con el
       estilo aquí, la nota nace bien el mismo día que sube este asset.

       POR QUÉ ASÍ Y NO EN UNA CAJA: es una LIMITACIÓN, y en este bloque el acento de marca
       ya significa OPORTUNIDAD (el borde de la tarjeta elegida, el filete de «OFERTA DEL
       DÍA», el chip de variante); teñirla del mismo color invierte el código de la
       pantalla. Texto pequeño, gris y centrado — el mismo molde que `.cco-nota-2x1` usa
       dentro del formulario (cod-co.css).

       CONTRASTE MEDIDO sobre el fondo del bloque en Colombia (#101418): #B0B0B0 da 8.6:1,
       muy por encima del 4.5:1 de AA. Es el gris que ya lleva `.ccl-vars__mas`: cero
       colores nuevos. `flex-basis` al 100% porque `.lb-packs` es flex en una de las dos
       plantillas del bloque. ⚠️ Todo en px (en este tema 1rem = 12px:
       `--font-body-scale: 1.2`, medido el 10-sep; este comentario decía 11). */
    // 13px y no 11px: aquí no es una coletilla al pie, es lo primero que se lee del
    // bloque, y a 11px sobre el título de 48px se leería como un pie de página perdido.
    // Margen abajo (no arriba): la separa de la primera tarjeta; del título ya la separa
    // el aire de su propia rayita.
    p.style.cssText = 'flex:1 0 100%;margin:0 0 12px;font-size:13px;line-height:1.45;' +
                      'color:#B0B0B0;text-align:center;';
    // MISMO TEXTO, palabra por palabra, que la nota del formulario (`.cco-nota-2x1`).
    // Dos redacciones para la misma restricción se leen como dos restricciones.
    p.textContent = 'El 2x1 no es acumulable con otros descuentos de la tienda.';
    /* ── DÓNDE VA: JUSTO DEBAJO DEL TÍTULO (dictado del dueño 09-sep) ────────
       «Va justo debajo del título OFERTA DEL DÍA, debajo de su rayita decorativa, antes
       de la primera tarjeta.» Antes cerraba el bloque, y ahí la leía quien ya había
       elegido; arriba la lee quien está a punto de elegir, que es cuando sirve.

       🔑 LA RAYITA NO ES UN ELEMENTO: es el `::after` de `.lb-packs__title` (medido en
       la tienda viva — el título mide 48px de alto y su pseudo-elemento va dentro). Por
       eso «debajo de la rayita» se consigue insertando DESPUÉS del título, sin buscar
       ningún nodo decorativo que no existe.

       Si el título no estuviera (otra plantilla del bloque mexicano), se cae a «antes de
       la primera tarjeta», que cumple igual la parte que importa: por encima de las
       ofertas, nunca perdida al final. */
    var titulo = caja.querySelector('.lb-packs__title');
    if (titulo && titulo.parentNode === caja) caja.insertBefore(p, titulo.nextSibling);
    else caja.insertBefore(p, caja.querySelector('.lb-tier'));
    return true;
  }

  // ── LA MINIATURA DE CADA TARJETA SIGUE A LA VARIANTE ELEGIDA ─────────────
  // El snippet mexicano pinta ahí `product.featured_image`, y en varias fichas
  // colombianas la destacada es una INFOGRAFÍA vertical: recortada al cuadrado de 60 px
  // queda un rectángulo oscuro con iconitos —lo que el dueño describió como «miniaturas
  // rotas con un ícono de descarga encima»—. La foto de la variante enseña lo que el
  // cliente se va a llevar. Se guarda la original en `data-ccl-src0` para poder volver a
  // ella cuando la variante elegida no tenga foto propia: así esto nunca deja una
  // tarjeta sin imagen.
  function pintarMiniaturas() {
    var V = variantes();
    if (V.length < 2) return false;
    var v = V[eleccionDe(0)];
    var url = (v && typeof v.imagen === 'string' && v.imagen) ? v.imagen : '';
    var imgs = document.querySelectorAll('.lb-tier__thumb');
    var toco = false;
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (!img.getAttribute('data-ccl-src0')) img.setAttribute('data-ccl-src0', img.getAttribute('src') || '');
      var destino = url || img.getAttribute('data-ccl-src0') || '';
      if (!destino) continue;
      if (img.getAttribute('src') !== destino) { img.setAttribute('src', destino); toco = true; }
    }
    return toco;
  }

  // ── EL SELECTOR VIEJO SIGUE SIENDO LA FUENTE DE `input[name=id]` ─────────
  // Está oculto, no borrado, y eso es a propósito: `syncVariant()` de landing-buda.js
  // escribe ahí la variante elegida, y de ese input lee `variantePack()` el botón
  // «Agregar al Carrito» (sección 2). Si los chips no lo empujaran, ese botón mandaría
  // al carrito la variante por defecto mientras el cliente ve otra elegida.
  // Se pulsa la píldora REAL —no se escribe el input— por la misma razón que todo lo
  // demás aquí: el estado interno del selector viejo sólo se mueve con su propio clic.
  // ⚠️ Medido en vivo el 07-oct: ese manejador (landing-buda.js `syncVariant`) mueve
  //    `input[name=id]` y la clase de la píldora, pero NO la foto grande de la galería del
  //    tema. Eso lo hace `moverFotoGrande` (abajo), y sólo en productos marcados.
  function sincronizarPildoras(j) {
    var V = variantes();
    var v = V[j];
    if (!v || !Array.isArray(v.optionValues)) return;
    for (var k = 0; k < v.optionValues.length; k++) {
      var ov = v.optionValues[k];
      if (!ov || !ov.optionName) continue;
      var pills = document.querySelectorAll('.lb-pill[data-option-name]');
      for (var m = 0; m < pills.length; m++) {
        var pill = pills[m];
        if (pill.getAttribute('data-option-name') !== ov.optionName) continue;
        if (pill.getAttribute('data-option-value') !== ov.name) continue;
        if (!pill.classList.contains('is-active')) pill.click();
        break;
      }
    }
  }

  // ── LA FOTO GRANDE SIGUE A LA PULSERA ELEGIDA (dueño 07-oct, OK explícito) ─────
  // SÓLO en los productos MARCADOS (metafield booleano `landing.portada_primero`; hoy sólo
  // PULSERAS SUTILES CON PROPÓSITO). En ellos `sections/main-product.liquid` pinta
  // `data-portada-primero` en <media-gallery> y un JSON `[[idVariante, idMedia], …]`
  // (`script[data-portada-variantes]`). Sin esa marca esta función sale en su 1.ª línea
  // y la ficha se comporta EXACTAMENTE como antes: ni una clase, ni un nodo movido.
  //
  // POR QUÉ HACE FALTA: en las fichas contra entrega el selector nativo de Dawn está
  // deshabilitado; el cliente elige en «Unidad 1 / Unidad 2» de la tarjeta (este archivo)
  // o dentro del formulario COD. Ninguno llegaba a la galería: la píldora oculta que pulsa
  // `sincronizarPildoras` sólo mueve el `input[name=id]` (medido 07-oct). Con la galería
  // abriendo en la PORTADA (el Liquid de arriba), elegir «Esencia» dejaba la portada.
  //
  // QUÉ FOTO: la de la ÚLTIMA pieza que el cliente tocó, sea la unidad 1 o la 2 («al elegir
  // una variante, se muestra la foto de la pulsera que escogió», dueño 07-oct). Variante sin
  // foto propia → la galería se queda como está.
  //
  // 🔒 MISMO GESTO QUE DAWN al cambiar de variante (`VariantSelects.updateMedia`, global.js):
  //    `setActiveMedia('<sección>-<media>', true)` —la foto pasa a la 1.ª posición, queda
  //    activa y la miniatura la acompaña— y el modal del zoom la antepone igual. No se
  //    escribe ninguna clase a mano: el contador, las miniaturas y el lector de pantalla
  //    quedan como los deja el propio tema.
  // 🔒 NO TOCA LA ELECCIÓN, NI EL PEDIDO, NI EL DINERO: sólo la galería. Corre DESPUÉS de
  //    guardar la elección y dentro de su propio `try`: si fallara, la elección sigue igual.
  function moverFotoGrande(j) {
    var g = document.querySelector('media-gallery[data-portada-primero]');
    if (!g) return false;
    var v = variantes()[j];
    if (!v || v.id == null) return false;
    var nodo = g.querySelector('script[data-portada-variantes]');
    if (!nodo) return false;
    var pares;
    try { pares = JSON.parse(nodo.textContent || '[]'); } catch (e) { return false; }
    if (!Array.isArray(pares)) return false;
    var mediaId = null;
    for (var k = 0; k < pares.length; k++) {
      var par = pares[k];
      if (Array.isArray(par) && String(par[0]) === String(v.id)) { mediaId = par[1]; break; }
    }
    if (mediaId == null) return false;
    var seccion = String(g.id || '').replace(/^MediaGallery-/, '');
    if (!seccion || seccion === g.id) return false;
    var destino = seccion + '-' + mediaId;
    if (typeof g.setActiveMedia !== 'function') return false;   // media-gallery.js aún no cargó
    if (!g.querySelector('[data-media-id="' + destino + '"]')) return false;
    // Dawn lee la miniatura SIN comprobar que exista: si la galería tiene miniaturas y falta
    // la de esta foto, no se llama (lanzaría a mitad y dejaría la galería a medias).
    var miniaturas = g.querySelector('[id^="GalleryThumbnails"]');
    if (miniaturas && !miniaturas.querySelector('[data-target="' + destino + '"]')) return false;
    g.setActiveMedia(destino, true);
    var modal = document.querySelector('#ProductModal-' + seccion + ' .product-media-modal__content');
    var enModal = modal ? modal.querySelector('[data-media-id="' + mediaId + '"]') : null;
    if (enModal) modal.prepend(enModal);
    return true;
  }

  // ── LLEVAR LA SELECCIÓN AL FORMULARIO, PULSANDO SUS CHIPS ────────────────
  // Espeja `aplicarTicks`: se busca el chip por su unidad y su índice de variante, y
  // sólo se pulsa si NO está ya elegido (un clic redundante gasta una recotización).
  // Sin chips en el formulario —producto de una sola variante, o un tier de piezas
  // fijas que el cliente no elige— no hay nada que pulsar y no se fuerza nada.
  /* 🐛 CHIP **O** DESPLEGABLE. Esta función empujaba la elección al formulario pulsando
     sus CHIPS, y salía por `return false` si no encontraba ninguno. En cuanto el umbral
     bajó a 2 (09-sep) el formulario pasó a usar <select> también con dos opciones, y
     esta sincronización se quedó muda: la ficha y el formulario podían acabar pidiendo
     piezas distintas. Lo destapó la suite —«la elección de la UNIDAD 2 cambia sólo en
     esa unidad» se puso roja— no una lectura del código. */
  function aplicarVariantes(cco) {
    var toco = false;
    var hayChips = cco.querySelectorAll('[data-cco-var-chip]').length > 0;
    var haySel = cco.querySelectorAll('[data-cco-var-sel]').length > 0;
    if (!hayChips && !haySel) return false;
    for (var i = 0; i < ELECCIONES.length; i++) {
      var j = ELECCIONES[i];
      if (!(j >= 0)) continue;
      if (hayChips) {
        var b = cco.querySelector('[data-cco-var-chip="' + i + '"][data-cco-var-idx="' + j + '"]');
        if (!b || b.disabled) continue;
        if (b.getAttribute('aria-pressed') === 'true') continue;
        b.click();
        toco = true;
        continue;
      }
      var d = cco.querySelector('[data-cco-var-sel="' + i + '"]');
      if (!d) continue;
      var op = d.options[j];
      if (!op || op.disabled) continue;
      if (String(d.value) === String(j)) continue;   // ya está: no se mueve nada
      d.value = String(j);
      // Se DISPARA el evento en vez de escribir el estado por dentro: así corre el
      // manejador del formulario (`elegirVariante`), que repinta y recotiza. Escribir
      // el valor a pelo dejaría la pantalla diciendo una cosa y el pedido pidiendo otra.
      d.dispatchEvent(new Event('change', { bubbles: true }));
      toco = true;
    }
    return toco;
  }

  // ── EL OYENTE ÚNICO DE LOS DOS SITIOS ────────────────────────────────────
  // Un solo listener, en CAPTURA sobre `document`, atiende las dos direcciones:
  //   · chip de la LANDING  → se guarda la elección, se repintan las tarjetas, se
  //     empuja la píldora oculta y, si el formulario está abierto, se pulsa su chip;
  //   · chip del FORMULARIO → se guarda la elección y se repintan las tarjetas.
  // Así la selección es UNA sola y no puede divergir entre las dos pantallas.
  //
  // 🔒 `preventDefault` + `stopPropagation` SÓLO para el chip de la landing: vive dentro
  // del <label> de la tarjeta y sin eso el clic acabaría seleccionando ese pack (que no
  // es lo que el cliente pidió al tocar «Caballero»). Es el mismo cuidado que el propio
  // bloque mexicano tiene con su stepper. NO se usa `stopImmediatePropagation`: los otros
  // oyentes de `document` de este archivo (incluido `tick`) tienen que seguir corriendo.
  /* El DESPLEGABLE de la landing (productos de 4+ opciones). Va por `change` y no por
     `click`: un `click` sobre un <select> se dispara al ABRIRLO, antes de que el cliente
     haya elegido. Termina en el MISMO sitio que el chip —`ELECCIONES[i]` y la misma
     cadena de repintado— para que no haya dos formas de elegir la pieza.
     🔒 `stopPropagation` por lo mismo que el chip: el desplegable vive dentro del
     <label> de la tarjeta y sin esto abrirlo seleccionaría ese pack. */
  document.addEventListener('change', function (ev) {
    if (aplicando) return;
    var t = ev.target;
    if (!t || !t.closest) return;
    // De la FICHA (`data-ccl-sel`) o del FORMULARIO (`data-cco-var-sel`): la elección es
    // UNA sola y se guarda en el mismo sitio, venga de la pantalla que venga. Sin la
    // segunda, cambiar la pieza dentro del formulario no se reflejaba en la tarjeta.
    var sel = t.closest('[data-ccl-sel]');
    var enForm = sel ? null : t.closest('[data-cco-var-sel]');
    if (!sel && !enForm) return;
    var origen = sel || enForm;
    var i = parseInt(origen.getAttribute(sel ? 'data-ccl-sel' : 'data-cco-var-sel'), 10);
    var j = parseInt(origen.value, 10);
    if (!(i >= 0) || !(j >= 0 && j < variantes().length)) return;
    if (sel) ev.stopPropagation();
    ELECCIONES[i] = j;
    aplicando = true;
    try {
      try { pintarChips(); } catch (e) {}
      try { pintarMiniaturas(); } catch (e) {}
      if (i === 0) { try { sincronizarPildoras(j); } catch (e) {} }
      // La foto grande enseña la pieza recién elegida (sólo productos marcados).
      try { moverFotoGrande(j); } catch (e) {}
      // Sólo se empuja al formulario cuando el cambio vino de la FICHA: si vino del
      // propio formulario, él ya está en su sitio y volver a empujarlo sería un bucle.
      if (sel) {
        var ccoSel = document.querySelector('[data-cco-root]');
        if (ccoSel && ccoSel.dataset.ccoListo === '1') { try { aplicarVariantes(ccoSel); } catch (e) {} }
      }
    } finally {
      aplicando = false;
    }
  }, true);

  /* 🐛 EL CLIC QUE ABRE EL DESPLEGABLE NO PUEDE LLEGAR A LA TARJETA.
     Reportado por el dueño (09-sep): «cuando escojo la 4ª unidad no me deja abrir el
     desplegable, se quita la 4ª opción». El <select> vive dentro del <label> de la
     tarjeta, que es clicable entera: al tocarlo, el clic burbujeaba hasta la tarjeta,
     ésta se «elegía» otra vez y `unidadesElegidas` volvía al MÍNIMO del pack (3). La
     tarjeta se repintaba con tres filas, la cuarta desaparecía y el desplegable que
     estaba abriendo se destruía debajo del dedo.
     Es el MISMO cuidado que ya tenían los chips; al pasar a <select> se me quedó sólo
     en el `change`, y el daño lo hace el `click`.
     🔒 `stopPropagation` SÍ, `preventDefault` NO: con preventDefault el desplegable no
     se abriría. */
  document.addEventListener('click', function (ev) {
    var s2 = (ev.target && ev.target.closest) ? ev.target.closest('[data-ccl-sel]') : null;
    if (s2) ev.stopPropagation();
  }, true);

  document.addEventListener('click', function (ev) {
    if (aplicando) return;
    var t = ev.target;
    if (!t || !t.closest) return;

    var enLanding = t.closest('[data-ccl-chip]');
    var enForm = enLanding ? null : t.closest('[data-cco-var-chip]');
    var b = enLanding || enForm;
    if (!b || b.disabled) return;

    var i = parseInt(b.getAttribute(enLanding ? 'data-ccl-chip' : 'data-cco-var-chip'), 10);
    var j = parseInt(b.getAttribute(enLanding ? 'data-ccl-idx' : 'data-cco-var-idx'), 10);
    if (!(i >= 0) || !(j >= 0 && j < variantes().length)) return;

    if (enLanding) {
      ev.preventDefault();
      ev.stopPropagation();
    }

    ELECCIONES[i] = j;
    aplicando = true;
    try {
      try { pintarChips(); } catch (e) {}
      try { pintarMiniaturas(); } catch (e) {}
      // El `input[name=id]` sólo tiene sentido para la unidad 1: es la única elección que
      // un carrito de Shopify (una línea, una variante) puede llevar. La foto grande, en
      // cambio, enseña la última pieza tocada (sólo productos marcados, `moverFotoGrande`).
      if (i === 0) { try { sincronizarPildoras(j); } catch (e) {} }
      try { moverFotoGrande(j); } catch (e) {}
      if (enLanding) {
        var cco = document.querySelector('[data-cco-root]');
        if (cco && cco.dataset.ccoListo === '1') { try { aplicarVariantes(cco); } catch (e) {} }
      }
    } finally {
      aplicando = false;
    }
  }, true);

  // 🔒 GUARD DE RE-ENTRADA. `limpiar()` escribe en el DOM y corre DENTRO del callback
  // del observador: sin esta bandera, cada escritura propia volvería a entrar y una
  // sola pasada podría anidarse hasta agotar la pila. Las cuatro operaciones son
  // idempotentes, así que perder una re-entrada no pierde ninguna limpieza: la
  // mutación siguiente vuelve a llamar.
  var enCurso = false;

  // 🔗 `?variant=` TAMBIÉN MUEVE LAS PÍLDORAS (dueño 02-oct). El selector viejo (landing-buda.js)
  // es la fuente de `input[name=id]` y de la foto grande; si el snippet del tema no trae la
  // corrección de Liquid, arrancaría en otra variante mientras los chips ya muestran la del
  // anuncio. Se pulsa la píldora REAL UNA vez (mismo gesto que un clic del cliente) y sólo
  // cuando la URL pide una variante válida; si ya estaba activa, `sincronizarPildoras` no hace
  // nada. Sin píldoras aún en el DOM se vuelve a intentar en el siguiente `tick`.
  //
  // 🐛 EL CLIC CAÍA EN EL VACÍO (verificado en vivo 02-oct, soybuda.com.co): este archivo carga
  // con `defer` y corre ANTES de DOMContentLoaded, pero `landing-buda.js` engancha el oyente de
  // cada píldora DENTRO de su `ready()` → DOMContentLoaded. Medido: clic a los 2435 ms con
  // readyState «interactive», oyentes enganchados a los 2679 ms. El clic no hacía nada, la
  // bandera quedaba en `true` y nadie lo repetía: la píldora activa y el `input[name=id]` de
  // `.lb-buy` se quedaban en la 1.ª variante (y «Agregar al carrito» compraba la que no era).
  // Arreglo, dos candados:
  //   1. no se pulsa hasta que el DOM esté listo (`domListo`, que se pone en un oyente de
  //      DOMContentLoaded registrado DESPUÉS que el de landing-buda.js —un <script> síncrono
  //      del cuerpo—, así que cuando corre, sus oyentes ya existen);
  //   2. se COMPRUEBA el efecto: si tras el clic la píldora de la URL no quedó activa, no se da
  //      por sembrado y se reintenta en el siguiente `tick` (máx. SEMBRADO_MAX intentos).
  // No se escribe la clase ni el input a mano: el estado interno del selector viejo (`selected`)
  // sólo se mueve con su propio clic, y escribirlo por fuera lo dejaría desincronizado.
  var sembradoUrl = false;
  var sembradoIntentos = 0;
  var SEMBRADO_MAX = 6;
  var domListo = document.readyState === 'complete';
  function pildorasEn(j) {
    var v = variantes()[j];
    if (!v || !Array.isArray(v.optionValues)) return true;
    for (var k = 0; k < v.optionValues.length; k++) {
      var ov = v.optionValues[k];
      if (!ov || !ov.optionName) continue;
      var pills = document.querySelectorAll('.lb-pill[data-option-name]');
      var hay = false, activa = false;
      for (var m = 0; m < pills.length; m++) {
        if (pills[m].getAttribute('data-option-name') !== ov.optionName) continue;
        if (pills[m].getAttribute('data-option-value') !== ov.name) continue;
        hay = true;
        if (pills[m].classList.contains('is-active')) activa = true;
      }
      if (hay && !activa) return false;
    }
    return true;
  }
  function sembrarVarianteUrl() {
    if (sembradoUrl) return;
    var u = varianteDeLaUrl();
    if (u < 0) { if (variantes().length) sembradoUrl = true; return; }
    if (!domListo) return;
    if (!document.querySelector('.lb-pill[data-option-name]')) return;
    sembradoIntentos++;
    aplicando = true;
    try { sincronizarPildoras(u); } finally { aplicando = false; }
    if (pildorasEn(u) || sembradoIntentos >= SEMBRADO_MAX) sembradoUrl = true;
  }

  function tick() {
    if (enCurso) return;
    enCurso = true;
    // El reset va en `finally` y NO al final del cuerpo. Hoy los dos `try` internos se
    // tragan todo, así que daría igual; pero el día que alguien añada una línea suelta
    // aquí en medio y lance, la bandera se quedaría en `true` PARA SIEMPRE y la capa
    // entera moriría EN SILENCIO — volvería la 🇲🇽 y nadie se enteraría, que es
    // exactamente el modo de fallo que costó esta tanda.
    try {
      try { limpiar(); } catch (e) {}
      try { montarAtc(); } catch (e) {}
      try { sembrarVarianteUrl(); } catch (e) {}
      try { pintarChips(); } catch (e) {}
      try { pintarMiniaturas(); } catch (e) {}
      try { pintarNota2x1(); } catch (e) {}
      // VA AL FINAL A PROPOSITO: mide, y lo que mide depende de lo que las cuatro de
      // arriba acaban de escribir (la L2 la compone `limpiar()` y el boton lo monta
      // `montarAtc()`). Medir antes seria medir el DOM de la pasada anterior.
      try { igualarAltoBotones(); } catch (e) {}
    } finally {
      enCurso = false;
    }
  }

  document.addEventListener('DOMContentLoaded', function () { domListo = true; tick(); });
  window.addEventListener('load', function () { domListo = true; tick(); });
  tick();
  // ⚡ VELOCIDAD (21-oct-2026). Este archivo ya no va `defer` sino `async`: puede ejecutarse
  // mientras el HTML todavía se está leyendo, y cada trozo que el lector inserta despierta al
  // observador. Mientras la página se LEE (`loading`) esas pasadas se juntan en una por
  // fotograma (antes de pintar): en ese tramo todavía no corre nada de landing-buda.js (su
  // `ready()` espera a DOMContentLoaded), así que no hay ninguna 🇲🇽 que repintar a destiempo.
  // En cuanto la página está leída, vuelve a ser SÍNCRONO como siempre (ver arriba: es lo que
  // le gana la carrera a `asegurarBanderaCOD()`). El observador se conecta YA, no en
  // DOMContentLoaded, para que esté escuchando cuando landing-buda.js repinte allí.
  var tickAgendado = false;
  function alMutar() {
    if (document.readyState !== 'loading' || typeof window.requestAnimationFrame !== 'function') { tick(); return; }
    if (tickAgendado) return;
    tickAgendado = true;
    window.requestAnimationFrame(function () { tickAgendado = false; tick(); });
  }
  try {
    new MutationObserver(alMutar).observe(document.documentElement, {
      childList: true, subtree: true, characterData: true
    });
  } catch (e) {
    // Sin MutationObserver (navegador muy viejo): red mínima por reloj.
    var pasos = 0, iv = setInterval(function () { tick(); if (++pasos > 40) clearInterval(iv); }, 500);
  }
  // Los dos gestos que disparan un repintado del bloque mexicano, en CAPTURA, como
  // cinturón por si el observador se hubiera montado tarde.
  document.addEventListener('change', tick, true);
  document.addEventListener('click', tick, true);
  // Y EL TERCERO, que no es una mutacion y por eso el observador no lo ve: girar el
  // telefono o cambiar de ancho mueve las media queries del token de la letra → la
  // píldora cambia de alto y el alto fijado al hermano se queda viejo. Se llama SOLO a
  // `igualarAltoBotones()` (no al tick entero): es una lectura y, si nada cambio, ni
  // escribe. `passive` porque no se cancela nada.
  window.addEventListener('resize', function () { try { igualarAltoBotones(); } catch (e) {} }, { passive: true });

  // Fin del arranque: el interceptor de la píldora ya está puesto (`data-cco-puente`). Si un
  // toque llegó antes y lo tiene la cola del <head> (snippets/cod-co-temprano.liquid), que lo
  // repita ahora si ya está todo.
  try { if (window.CcoCola && typeof window.CcoCola.avisar === 'function') window.CcoCola.avisar(); } catch (e) { /* */ }
})();
